import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import FileUpload from '@aytm/app/stats/lib/io/file-upload'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/word-cloud/styles/MaskUploader.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const MaskUploader = ({ id, onUploaded, onCancel }) => {
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)

  const inputRef = useRef(null)

  // Lazy-init on first render — avoids the null window that would otherwise exist
  // between initial render and the mount effect commit (§D.4).
  const abortControllerRef = useRef(null)
  if (abortControllerRef.current === null) {
    abortControllerRef.current = new AbortController()
  }

  const onChange = () => {
    if (inputRef.current && inputRef.current.files && 0 < inputRef.current.files.length) {
      setProcessing(true)

      const abortSignal = abortControllerRef.current?.signal

      FileUpload({
        file: inputRef.current.files[0],
        uploadUrl: PageGlobals.CREATE_WORD_CLOUD_PATH,
        params: { image_type: 'wc_mask' },
        abortSignal
      })
        .then(
          data => {
            setProcessing(false)
            onUploaded(data)
          },
          null,
          (progress, totalProgress) => {
            setProgress(parseInt((100 * progress) / totalProgress, 10))
          }
        )
        .catch(() => setProcessing(false))
    }
  }

  // Abort any in-flight upload on unmount.
  useEffect(() => () => abortControllerRef.current?.abort(), [])

  return (
    <div className={style.component}>
      {processing ? (
        <span data-testid={`stat-wordcloud-${id}-mask-custom-progress`}>Uploading {progress}%</span>
      ) : (
        <>
          <span className="p-component p-button p-button-rounded p-button-blue">
            Upload
            <input
              ref={inputRef}
              data-testid={`stat-wordcloud-${id}-mask-custom-upload`}
              type="file"
              name="file"
              accept="*"
              multiple={false}
              tabIndex={-1}
              onChange={onChange}
            />
          </span>
          <Popup
            width="300px"
            trigger="click"
            direction="bottomright"
            popupOffset={9}
            hideOtherPopupsOnShow
            content={() => (
              <>
                To create a word cloud within a custom shape, upload an image with a black silhouette on a transparent
                or white background. Black shapes will be filled with letters, while your white/transparent background
                will be left empty. Simple shapes will take less time to process. Ideal dimensions: 640 x 855 pixels
                (vertical) or 640 x 425 pixels (horizontal). We recommend using PNG format, but you can also upload JPG,
                SVG, PDF, PSD, or AI. If you want to save a mask, click the &quot;Save&quot; button below, and your mask
                will be remembered as a custom preset until you overwrite it with a new one.
              </>
            )}
          >
            <i className="icon-aytmfd-help-v2" />
          </Popup>
          <span
            data-testid={`stat-wordcloud-${id}-mask-custom-upload-cancel`}
            className={style.delete}
            aria-label="Cancel upload"
            {...pressable(onCancel)}
          >
            <i className="icon-aytmfd-x-v2" />
          </span>
        </>
      )}
    </div>
  )
}

MaskUploader.propTypes = {
  id: PropTypes.string,
  onUploaded: PropTypes.func,
  onCancel: PropTypes.func
}

MaskUploader.displayName = 'MaskUploader'

export default MaskUploader
